import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useForm,
  FormProvider,
  useWatch,
  type Path,
  type Resolver,
} from "react-hook-form";
import { differenceInCalendarDays, getDay, isValid, parse } from "date-fns";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import {
  Clubhouse_LeagueFacilityScope,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueType,
  Clubhouse_LeagueWizardScheduleMode,
} from "../enums";
import { leagueFormSchema, type LeagueFormValues } from "./leagueFormSchema";
import { mergePlanWithEdits } from "./advanced/roundPlan";
import { useRoundPlan } from "./advanced/useRoundPlan";
import { isSeasonFlexAllowed } from "./helpers";
import { WizardStepper } from "./common/WizardStepper";
import { LeagueDatesStep } from "./steps/LeagueDatesStep";
import { LeagueReviewStep } from "./steps/LeagueReviewStep";
import { LeagueFormatStep } from "./steps/LeagueFormatStep";
import { LeagueScoringStep } from "./steps/LeagueScoringStep";
import { LeagueFacilityStep } from "./steps/LeagueFacilityStep";
import { LeaguesV2Events } from "../analytics/leaguesV2Events";
import { useTrackLeaguesV2View } from "../analytics/useTrackLeaguesV2View";

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

type StepKey = "format" | "scoring" | "schedule" | "location" | "review";

interface StepDef {
  title: string;
  heading: string;
  subhead?: string;
  fields: Path<LeagueFormValues>[];
}

const STEPS: Record<StepKey, StepDef> = {
  format: {
    title: "Format",
    heading: "What type of league are you running?",
    fields: ["wizard.leagueType"],
  },
  scoring: {
    title: "Scoring",
    heading: "How will you score this league?",
    fields: ["wizard.leagueScoringType"],
  },
  schedule: {
    title: "Schedule",
    heading: "League Schedule",
    fields: ["leagueStartDate", "leagueEndDate", "wizard.scheduleMode"],
  },
  location: {
    title: "Location",
    heading: "Where can this league be played?",
    fields: ["facilityScope"],
  },
  review: {
    title: "Review",
    heading: "Review your league",
    subhead: "Double-check everything below, then create your league.",
    fields: [],
  },
};

/**
 * Guided Setup asks for the format, how it is scored, when it runs and — for an
 * operator with more than one facility — where. Everything else a league needs
 * is applied for them (see `CreateLeaguePage`), because the whole promise of
 * this flow is a league in a few answers.
 *
 * Scramble scores one shared ball, so it has no scoring question; an operator
 * with a single facility has no location question. Both steps drop out of the
 * flow and out of the stepper rather than being shown and skipped.
 */
function visibleSteps(
  leagueType: Clubhouse_LeagueType | undefined,
  hasMultipleFacilities: boolean,
): StepKey[] {
  return [
    "format" as const,
    ...(leagueType === Clubhouse_LeagueType.SCRAMBLE
      ? []
      : ["scoring" as const]),
    "schedule" as const,
    ...(hasMultipleFacilities ? ["location" as const] : []),
    "review" as const,
  ];
}

/** Whether the step's own question has been answered at all. */
function isStepAnswered(
  key: StepKey,
  values: {
    leagueType?: Clubhouse_LeagueType;
    scoring?: Clubhouse_LeagueScoring;
    start?: string;
    end?: string;
    scheduleMode?: Clubhouse_LeagueWizardScheduleMode;
    facilityScope?: Clubhouse_LeagueFacilityScope;
  },
): boolean {
  switch (key) {
    case "format":
      return values.leagueType != null;
    case "scoring":
      return values.scoring != null;
    case "schedule":
      return !!values.start && !!values.end && values.scheduleMode != null;
    case "location":
      return values.facilityScope != null;
    case "review":
      return true;
  }
}

interface GuidedRoundPlan {
  /** The rounds to create the league with, once they can be trusted. */
  seasonRounds: LeagueFormValues["seasonRounds"];
  /** The facility's courses are still on their way; waiting will fix this. */
  loading: boolean;
}

export interface CreateLeagueWizardProps {
  onSubmit: (values: LeagueFormValues) => void | Promise<void>;
  onBack?: () => void;
  isSubmitting?: boolean;
  initialName?: string;
  initialDescription?: string;
  /** Drives whether the Location step is asked at all. */
  hasMultipleFacilities?: boolean;
}

export function CreateLeagueWizard({
  onSubmit,
  onBack,
  isSubmitting = false,
  initialName = "",
  initialDescription = "",
  hasMultipleFacilities = false,
}: CreateLeagueWizardProps) {
  const facilityId = useGetCurrentLocalFacilityId();
  const [step, setStep] = useState(0);
  // The furthest step reached: the stepper walks back to those and no further,
  // because a step ahead is one whose answers do not exist yet.
  const [reached, setReached] = useState(0);

  const form = useForm<LeagueFormValues>({
    resolver: zodResolver(leagueFormSchema) as Resolver<LeagueFormValues>,
    mode: "onBlur",
    defaultValues: {
      name: initialName,
      description: initialDescription,
      leagueStartDate: "",
      leagueEndDate: "",
      facilityId: facilityId,
      // A single-facility operator is never asked, so the league is created at
      // the facility they are working in.
      facilityScope: Clubhouse_LeagueFacilityScope.THIS_FACILITY,
    },
  });
  const { control, setValue } = form;

  const leagueType = useWatch({ control, name: "wizard.leagueType" });
  const scoring = useWatch({ control, name: "wizard.leagueScoringType" });
  const start = useWatch({ control, name: "leagueStartDate" });
  const end = useWatch({ control, name: "leagueEndDate" });
  const scheduleMode = useWatch({ control, name: "wizard.scheduleMode" });
  const facilityScope = useWatch({ control, name: "facilityScope" });

  // Scramble skips the Scoring step, so the wizard answers it: a shared ball
  // only scores as Stroke Play.
  useEffect(() => {
    if (leagueType === Clubhouse_LeagueType.SCRAMBLE) {
      setValue("wizard.leagueScoringType", Clubhouse_LeagueScoring.STROKE);
    }
  }, [leagueType, setValue]);

  // Match Play needs scheduled rounds, so a scoring format chosen after the
  // schedule clears a Flex that is no longer allowed. It belongs here and not
  // on the Schedule step: the operator can change the format and walk straight
  // to Review from the stepper, and that step would never render to correct it.
  useEffect(() => {
    if (
      !isSeasonFlexAllowed(scoring) &&
      scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex
    ) {
      setValue(
        "wizard.scheduleMode",
        Clubhouse_LeagueWizardScheduleMode.WeeklyRounds,
      );
    }
  }, [scoring, scheduleMode, setValue]);

  const steps = useMemo(
    () => visibleSteps(leagueType, hasMultipleFacilities),
    [leagueType, hasMultipleFacilities],
  );
  // The facilities query can resolve after the flow has started, which adds the
  // Location step underneath the operator; never leave the index past the end.
  const current = Math.min(step, steps.length - 1);
  const key = steps[current]!;
  useTrackLeaguesV2View(LeaguesV2Events.VIEW_CREATE_WIZARD_STEP, {
    step: key,
    stepNumber: current + 1,
  });
  const def = STEPS[key];
  const isLast = current === steps.length - 1;
  const answered = isStepAnswered(key, {
    leagueType,
    scoring,
    start,
    end,
    scheduleMode,
    facilityScope,
  });

  const goTo = (next: number) => {
    setStep(next);
    setReached((r) => Math.max(r, next));
  };

  const handleNext = async () => {
    const ok = def.fields.length === 0 || (await form.trigger(def.fields));
    if (ok) goTo(current + 1);
  };

  const { seasonRounds, loading: roundsLoading } = useGuidedRoundPlan({
    start,
    end,
    scheduleMode,
    scoring,
  });
  const canCreate = !!seasonRounds;

  // Re-validate every collected field before POST — a value can turn invalid
  // after the step that owns it (e.g. a start date that was "today" when picked
  // but rolled past UTC midnight while the operator worked). Only the step
  // fields: schema fields the submit mapping fills in (timezones,
  // seasonRoundCount, …) aren't in the form yet, so validating the whole schema
  // here would always fail and wrongly bounce. On failure, jump to the earliest
  // step with an error so its message is visible instead of POSTing blindly.
  const handleCreate = async () => {
    const stepFields = steps.flatMap((s) => STEPS[s].fields);
    if (!(await form.trigger(stepFields))) {
      const badStep = steps.findIndex((s) =>
        STEPS[s].fields.some((f) => form.getFieldState(f).invalid),
      );
      goTo(badStep === -1 ? steps.indexOf("schedule") : badStep);
      return;
    }
    // The button is disabled without them; this is the belt to that braces, so
    // no later refactor can post a league with no round settings.
    if (!seasonRounds) return;
    await onSubmit({ ...form.getValues(), seasonRounds });
  };

  return (
    <FormProvider {...form}>
      <div className="flex flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="poppins flex items-center gap-2 text-sm font-semibold text-text-muted">
            Create League
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-text-primary">
              Guided Setup
            </span>
          </p>
          {/* The page's own h1 steps aside for Guided (see CreateLeaguePage),
              so the step's heading is the heading of the page. */}
          <h1 className="poppins m-0 text-2xl font-extrabold tracking-tight text-text-figure">
            {def.heading}
          </h1>
          {def.subhead && (
            <p className="mulish m-0 text-sm text-text-muted">{def.subhead}</p>
          )}
        </div>

        <WizardStepper
          steps={steps.map((s) => STEPS[s].title)}
          current={current}
          reached={Math.min(reached, steps.length - 1)}
          onSelect={setStep}
        />

        <div className="w-full rounded-2xl border border-border bg-card p-6 shadow-sm">
          {key === "format" && <LeagueFormatStep />}
          {key === "scoring" && <LeagueScoringStep />}
          {key === "schedule" && <LeagueDatesStep />}
          {key === "location" && <LeagueFacilityStep />}
          {key === "review" && (
            <LeagueReviewStep showLocation={steps.includes("location")} />
          )}
        </div>

        {isLast && (
          <p className="mulish text-sm text-text-primary">
            {canCreate
              ? "Your league is created as a draft. Next, add players and finalize it to open it for play."
              : roundsLoading
                ? "Preparing this league's rounds. Create League opens in a moment."
                : "This league's rounds could not be prepared. Reload the page and try again."}
          </p>
        )}

        <div className="flex w-full justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              current === 0 && onBack ? onBack() : setStep(current - 1)
            }
            disabled={current === 0 && !onBack}
            className="rounded-md bg-primary/6 pl-3 pr-4 font-bold text-primary"
          >
            <ChevronLeft className="size-4" />
            Back
          </Button>

          {isLast ? (
            <Button
              type="button"
              onClick={() => void handleCreate()}
              disabled={isSubmitting || !canCreate}
              className="poppins rounded-md bg-primary font-semibold tracking-tight text-primary-foreground active:scale-[0.97] motion-safe:hover:-translate-y-px motion-safe:hover:shadow-md"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Creating…
                </>
              ) : (
                "Create League"
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void handleNext()}
              disabled={!answered}
              className="group/next poppins rounded-md bg-primary pl-4 pr-3 font-semibold tracking-tight text-primary-foreground active:scale-[0.97] motion-safe:hover:-translate-y-px motion-safe:hover:shadow-md"
            >
              Next
              <ChevronRight className="size-4 motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out motion-safe:group-hover/next:translate-x-0.5" />
            </Button>
          )}
        </div>
      </div>
    </FormProvider>
  );
}

/**
 * The rounds a Guided league is created with.
 *
 * Guided asks for no round settings, and a league whose rounds carry none is
 * created with empty conditions — so it generates the same plan Advanced Setup
 * would for these dates and applies Randomize to it: every round gets the
 * facility-default automation plus its own weather, deterministically by round
 * number (see roundConditions.ts).
 *
 * The week day Guided never asks for is the season's own start day, so a league
 * that starts on a Tuesday plays on Tuesdays. Flex gets one round per season
 * week, which is the same count Advanced derives from the dates.
 *
 * Until the plan is in hand there are no rounds to create a league with, and
 * creating one anyway is what left Guided leagues with empty round settings in
 * the first place — so the wizard holds Create rather than posting without
 * them. `loading` separates the ordinary wait for the facility's courses from
 * the case where they arrived and still produced no usable plan, which the
 * operator cannot fix by waiting.
 */
function useGuidedRoundPlan({
  start,
  end,
  scheduleMode,
  scoring,
}: {
  start?: string;
  end?: string;
  scheduleMode?: Clubhouse_LeagueWizardScheduleMode;
  scoring?: Clubhouse_LeagueScoring;
}): GuidedRoundPlan {
  const startDate = start ? parse(start, "yyyy-MM-dd", new Date()) : undefined;
  const endDate = end ? parse(end, "yyyy-MM-dd", new Date()) : undefined;
  const datesValid =
    !!startDate && isValid(startDate) && !!endDate && isValid(endDate);

  const { plan, coursesReady } = useRoundPlan({
    seasonStart: start,
    seasonEnd: end,
    scheduleMode,
    weekDay: datesValid ? getDay(startDate) : undefined,
    flexRoundCount: datesValid
      ? Math.min(
          Math.max(
            1,
            Math.ceil((differenceInCalendarDays(endDate, startDate) + 1) / 7),
          ),
          52,
        )
      : undefined,
    varyConditions: true,
    ready: datesValid && scheduleMode != null,
  });

  const seasonRounds = useMemo(() => {
    if (!plan || plan.rounds.length === 0) return undefined;
    // Every facility is licensed at least one course, so a round with no course
    // means the course list is not in hand yet, not that there is nothing to
    // play. Either way these rounds are not fit to create a league with.
    if (plan.rounds.some((r) => !r.courseId)) return undefined;
    return mergePlanWithEdits(
      plan.rounds,
      [],
      scoring ?? Clubhouse_LeagueScoring.STROKE,
    );
  }, [plan, scoring]);

  return { seasonRounds, loading: !seasonRounds && !coursesReady };
}
